import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import StyledFooterMain from './FooterMain.styled';
import { Link } from 'react-router-dom';
import SVGIcon from '../utility/SVGIcon';
import { storeName } from '../../config/constants';
import { openExternalLinksInNewTab } from '../../helpers/externalLinks';
import HtmlContent from '../HtmlContent/HtmlContent';

const FooterMain = React.memo((props) => {
  const { license_number, simple, pickupLocations, deliveryAreas, footer, message, onSubscribe } = props;
  const currentYear = new Date().getFullYear();
  const emailRef = useRef();

  useEffect(() => {
    if (message === 'Success' && emailRef.current) {
      emailRef.current.value = '';
    }
  }, [message]);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (onSubscribe) {
      onSubscribe({ email: e.target.email.value.trim() });
    }
  };

  const handleSamePageClick = (to) => () => {
    if (window.location.pathname === to) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const bottomBar = (
    <div className="footer-bottom">
      <p className="footer-bottom__copy">
        &copy; {currentYear} {storeName}. All rights reserved.
      </p>
      <div className="footer-bottom__links">
        <Link to="/privacy">Privacy policy</Link>
        <Link to="/terms">Terms of service</Link>
      </div>
    </div>
  );

  if (simple) {
    return (
      <StyledFooterMain simple data-testid="footerMain">
        {bottomBar}
      </StyledFooterMain>
    );
  }

  return (
    <StyledFooterMain data-testid="footerMain">
      <div className="footer-inner">
        <div className="footer-top">
          <div className="footer-col footer-col--brand">
            <Link to="/">
              <h2 className="footer-brand__name">{storeName.toUpperCase()}</h2>
            </Link>
            {footer?.about ? (
              <HtmlContent
                className="footer-brand__about"
                html={openExternalLinksInNewTab(footer.about)}
              />
            ) : (
              <p className="footer-brand__about">
                Your trusted source for premium cannabis in Los Angeles. Flower, vapes, edibles, and more from the
                brands you love.
              </p>
            )}
            <div className="footer-brand__socials">
              {footer?.instagram_url && (
                <a href={footer.instagram_url} target="_blank" rel="noopener noreferrer" className="footer-social-link">
                  <span className="t-hidden">Instagram</span>
                  <SVGIcon name="instagram" />
                </a>
              )}
              {footer?.twitter_url && (
                <a href={footer.twitter_url} target="_blank" rel="noopener noreferrer" className="footer-social-link">
                  <span className="t-hidden">X</span>
                  <SVGIcon name="twitter" />
                </a>
              )}
            </div>
          </div>

          <div className="footer-col">
            <h4 className="footer-col__heading">QUICK LINKS</h4>
            <ul className="footer-col__list">
              <li>
                <Link to="/about">About us</Link>
              </li>
              <li>
                <Link to="/contact">Contact</Link>
              </li>
              <li>
                <Link to="/careers">Careers</Link>
              </li>
              <li>
                <Link to="/rewards">Rewards</Link>
              </li>
              <li>
                <Link to="/blog">Blog</Link>
              </li>
            </ul>
          </div>

          <div className="footer-col">
            <h4 className="footer-col__heading">SHOP BY</h4>
            <ul className="footer-col__list">
              <li>
                <Link to="/shop">All products</Link>
              </li>
              <li>
                <Link to="/brands">Brands</Link>
              </li>
              <li>
                <Link to="/promotions">Promotions</Link>
              </li>
            </ul>
          </div>
          {pickupLocations && pickupLocations.length > 0 && (
            <div className="footer-col">
              <h4 className="footer-col__heading">OUR LOCATIONS</h4>
              <ul className="footer-col__list">
                {pickupLocations.map((loc, i) => (
                  <li key={i} className="footer-col__location">
                    <Link to={`/dispensary/${loc.uri_param}`}>{loc.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {deliveryAreas && deliveryAreas.length > 0 && (
          <>
            <hr className="footer-divider" />
            <div className="footer-delivery-areas">
              <h4 className="footer-col__heading">WEED DELIVERY AREAS</h4>
              <ul className="footer-delivery-areas__list">
                {deliveryAreas.map((area) => {
                  const to = `/weed-delivery/${area.uri_param}`;
                  return (
                    <li key={area.uri_param} className="footer-delivery-areas__item">
                      <Link to={to} title={area.title} onClick={handleSamePageClick(to)}>
                        {area.city_name}
                      </Link>
                    </li>
                  );
                })}
                <li className="footer-delivery-areas__item footer-delivery-areas__item--all">
                  <Link to="/weed-delivery" onClick={handleSamePageClick('/weed-delivery')}>
                    View all areas →
                  </Link>
                </li>
              </ul>
            </div>
          </>
        )}

        <hr className="footer-divider" />

        <div className="footer-mid">
          <div className="footer-newsletter">
            <h4 className="footer-col__heading">STAY IN THE LOOP</h4>
            <p className="footer-newsletter__copy">Exclusive drops, deals, and rewards in your inbox.</p>
            <form className="footer-newsletter__form" onSubmit={handleSubscribe}>
              <label className="t-hidden" htmlFor="footerEmail">
                Enter your email address
              </label>
              <input
                className="footer-newsletter__input"
                id="footerEmail"
                name="email"
                type="email"
                required
                placeholder="your@email.com"
                ref={emailRef}
              />
              <button type="submit" className="footer-newsletter__btn">
                Subscribe
              </button>
            </form>
          </div>

          {license_number && (
            <div className="footer-license">
              <h4 className="footer-col__heading">LICENSE INFO</h4>
              <p className="footer-license__text">{license_number}</p>
            </div>
          )}
        </div>

        <hr className="footer-divider" />

        {bottomBar}
      </div>
    </StyledFooterMain>
  );
});

FooterMain.propTypes = {
  license_number: PropTypes.string,
  simple: PropTypes.bool,
  pickupLocations: PropTypes.array,
  deliveryAreas: PropTypes.arrayOf(
    PropTypes.shape({
      city_name: PropTypes.string,
      title: PropTypes.string,
      uri_param: PropTypes.string,
    })
  ),
  footer: PropTypes.shape({
    about: PropTypes.string,
    twitter_url: PropTypes.string,
    instagram_url: PropTypes.string,
  }),
  message: PropTypes.string,
  onSubscribe: PropTypes.func,
};

export default FooterMain;
